import { useMarketTicker, type TickerItem as TickerItemType } from '@/hooks/use-market-ticker';

/**
 * Global Market Ticker — scrolls across every authenticated page between
 * Header and main content. Silent-failure: loading / error / empty → null
 * (no degraded bar). Data refreshes every 5 min via useMarketTicker's
 * refetchInterval.
 */
export function MarketTicker() {
  const { data, isLoading, isError } = useMarketTicker();

  if (isLoading || isError || !data?.items?.length) {
    return null;
  }

  // After the guard above, data + data.items are both defined with length > 0.
  // Duplicate the list so the translateX(-50%) loop is seamless.
  const loopItems = [...data.items, ...data.items];

  return (
    <div
      className="shrink-0 h-9 border-b border-panel-border bg-void overflow-hidden"
      data-testid="market-ticker"
    >
      <div className="flex animate-ticker-scroll hover:[animation-play-state:paused] h-full items-center">
        {loopItems.map((item, i) => (
          <TickerItemRow key={`${item.source}-${item.symbol}-${i}`} item={item} />
        ))}
      </div>
    </div>
  );
}

function TickerItemRow({ item }: { item: TickerItemType }) {
  const positive = (item.change_pct ?? 0) > 0;
  const negative = (item.change_pct ?? 0) < 0;
  const color = positive ? 'text-accent' : negative ? 'text-danger' : 'text-text-dim';
  const arrow = positive ? '▲' : negative ? '▼' : '·';

  return (
    <div
      className="inline-flex items-center gap-2 px-4 text-xs font-mono tabular-nums whitespace-nowrap border-r border-panel-border h-full"
      data-testid="market-ticker-item"
      data-symbol={item.symbol}
    >
      <span className="text-text-muted">{item.symbol}</span>
      <span className="text-text-bright">{formatPrice(item)}</span>
      {item.change_abs !== null && item.change_pct !== null && (
        <span className={color} data-testid="market-ticker-change">
          {arrow} {formatAbsChange(item)} ({formatPctChange(item.change_pct)})
        </span>
      )}
    </div>
  );
}

function formatPrice(item: TickerItemType): string {
  if (item.category === 'fx') return item.price.toFixed(4);
  if (item.category === 'bonds') return `${item.price.toFixed(2)}%`;
  if (item.category === 'commodities') {
    const prefix = item.unit === '%' ? '' : '$';
    return `${prefix}${item.price.toFixed(2)}`;
  }
  if (item.category === 'crypto') {
    // Penny-denominated crypto (DOGE, XRP) get 4 decimals; BTC/ETH whole-ish
    const decimals = item.price < 10 ? 4 : 0;
    return `$${item.price.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  }
  // equities (indices): comma-separated, 2 decimals
  return item.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatAbsChange(item: TickerItemType): string {
  const abs = item.change_abs;
  if (abs === null) return '';
  const sign = abs > 0 ? '+' : '';
  if (item.category === 'fx') return `${sign}${abs.toFixed(4)}`;
  if (item.category === 'bonds') return `${sign}${abs.toFixed(2)}`;
  if (item.category === 'crypto' && Math.abs(abs) < 10) return `${sign}${abs.toFixed(4)}`;
  return `${sign}${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPctChange(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}
